import './style.css';
import { backend } from './services/backend.js';
import { ui } from './ui/interface.js';
import { storage } from './utils/storage.js';
import { validation } from './utils/validation.js';
import { notifications } from './ui/notifications.js';
import { getEnvConfig } from './config/env.js';
import categoriesService from './services/categoriesService.js';

// State management (functional approach)
const createAppState = () => ({
  isAuthenticated: false,
  isConnected: false,
  activities: [],
  filteredActivities: [],
  currentFilter: '',
  searchTerm: '',
  editingActivity: null,
  isLoading: false,
  stats: {}, // Will be populated dynamically from categories
});

let appState = createAppState();

// Set to true when the user removes the featured image via the X button;
// reset when the modal opens or a new file is selected
let removeFeaturedImage = false;

// Existing gallery images during edit: [{ url, id }], kept in sync with the
// preview; galleryModified becomes true when the user removes one via the X
let galleryItems = [];
let galleryModified = false;

// State update helpers
const updateState = (updates) => {
  appState = { ...appState, ...updates };
  renderApp();
};

const updateStats = async () => {
  // Build stats dynamically from loaded categories
  const categories = await categoriesService.getCategories();
  const stats = {};

  categories.forEach((cat) => {
    stats[cat.slug] = appState.activities.filter(
      (a) => a.categoria === cat.slug
    ).length;
  });

  updateState({ stats });
};

// Filter and search functions
const applyFilters = () => {
  let filtered = [...appState.activities];

  // Apply category filter
  if (appState.currentFilter) {
    filtered = filtered.filter(
      (activity) => activity.categoria === appState.currentFilter
    );
  }

  // Apply search filter
  if (appState.searchTerm) {
    const searchLower = appState.searchTerm.toLowerCase();
    filtered = filtered.filter(
      (activity) =>
        activity.nome.toLowerCase().includes(searchLower) ||
        activity.descrizione.toLowerCase().includes(searchLower) ||
        activity.telefono.includes(searchLower) ||
        activity.email.toLowerCase().includes(searchLower)
    );
  }

  updateState({ filteredActivities: filtered });
};

// Background preloading removed - activities are loaded automatically by testConnection()

// Main app initialization
const initializeApp = async () => {
  setupLogin();
};

// Login handling
const setupLogin = () => {
  // Check if user is already logged in
  const session = backend.auth.getSession();
  if (session && session.userId && session.token) {
    // Check if user is superadmin and redirect to superadmin panel
    if (session.user?.role === 'superadmin') {
      window.location.href = '/superadmin.html';
      return;
    }

    updateState({ isAuthenticated: true });
    showApp();
    return;
  }

  const form = document.getElementById('login-form');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(form).entries());
    const errorEl = document.getElementById('login-error');
    const submitBtn = form.querySelector('button[type="submit"]');

    errorEl.classList.add('hidden');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Accesso in corso...';

    try {
      // Call backend login API
      // Expected response: { userId, token, user: { id, username, email, role, ... } }
      const response = await backend.auth.login(data.user, data.password);

      if (response.userId && response.token && response.user) {
        // Check if user is superadmin
        if (response.user.role === 'superadmin') {
          // Redirect to superadmin panel
          window.location.href = '/superadmin.html';
          return;
        }

        // Normal user - show backoffice
        updateState({ isAuthenticated: true });
        showApp();
      } else {
        throw new Error('Login failed: invalid response');
      }
    } catch (error) {
      console.error('Login error:', error);
      errorEl.textContent = error.message || 'Credenziali errate o errore di connessione';
      errorEl.classList.remove('hidden');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Accedi';
    }
  });
};

// Load categories and populate select dropdowns
const loadCategories = async () => {
  try {
    const categories = await categoriesService.loadCategories();
    populateCategorySelects(categories);
  } catch (error) {
    console.error('❌ Failed to load categories:', error);
    notifications.error('Errore caricamento categorie');
  }
};

// Populate category select dropdowns dynamically
const populateCategorySelects = (categories) => {
  const filterSelect = document.getElementById('category-filter');
  const formSelect = document.getElementById('activity-category');

  if (filterSelect) {
    // Add "All" option for filter
    filterSelect.innerHTML = '<option value="">Tutte le categorie</option>';
    categories.forEach((cat) => {
      const option = document.createElement('option');
      option.value = cat.slug;
      option.textContent = `${cat.icon} ${cat.name}`;
      filterSelect.appendChild(option);
    });
  }

  if (formSelect) {
    // Add "Select" placeholder for form
    formSelect.innerHTML = '<option value="">Seleziona categoria</option>';
    categories.forEach((cat) => {
      const option = document.createElement('option');
      option.value = cat.slug;
      option.textContent = `${cat.icon} ${cat.name}`;
      formSelect.appendChild(option);
    });
  }
};

const showApp = async () => {
  document.getElementById('login-screen').classList.add('hidden');
  const root = document.getElementById('app-root');
  root.classList.remove('hidden');
  const header = document.getElementById('app-header');
  if (header) header.classList.remove('hidden');
  // Proceed with normal initialization
  await testConnection();
  await loadCategories(); // Load categories and populate selects
  setupEventListeners();
  renderApp();
};

// Logout
window.appLogout = () => {
  backend.auth.logout();
  location.reload();
};

// Event listeners setup
const setupEventListeners = () => {
  // No configuration events (env-managed)

  // Activity management events
  document
    .getElementById('add-activity-btn')
    .addEventListener('click', () => openActivityModal());
  document
    .getElementById('sync-btn')
    .addEventListener('click', () => loadActivities());
  document
    .getElementById('export-btn')
    .addEventListener('click', exportActivities);

  // Filter and search events
  document
    .getElementById('category-filter')
    .addEventListener('change', handleCategoryFilter);
  document
    .getElementById('search-input')
    .addEventListener('input', handleSearch);

  // Modal events
  document
    .getElementById('cancel-activity-btn')
    .addEventListener('click', closeActivityModal);
  document
    .getElementById('activity-form')
    .addEventListener('submit', handleActivitySubmit);

  // Image file preview listeners
  const imageInput = document.getElementById('activity-image-file');
  if (imageInput) {
    imageInput.addEventListener('change', () => {
      const file = imageInput.files && imageInput.files[0];
      const container = document.getElementById('activity-image-preview');
      if (!container) return;
      if (!file) {
        container.innerHTML = '';
        return;
      }
      removeFeaturedImage = false;
      const url = URL.createObjectURL(file);
      container.innerHTML = ui.renderImagePreview(url);
    });
  }

  // Remove featured image (X button on preview)
  const imagePreviewContainer = document.getElementById('activity-image-preview');
  if (imagePreviewContainer) {
    imagePreviewContainer.addEventListener('click', (e) => {
      if (!e.target.closest('#remove-image-btn')) return;
      removeFeaturedImage = true;
      if (imageInput) imageInput.value = '';
      imagePreviewContainer.innerHTML = '';
    });
  }

  // Remove a gallery image (X button on each preview)
  const galleryPreviewContainer = document.getElementById('activity-gallery-preview');
  if (galleryPreviewContainer) {
    galleryPreviewContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-gallery-remove]');
      if (!btn) return;
      galleryItems.splice(Number(btn.dataset.galleryRemove), 1);
      galleryModified = true;
      galleryPreviewContainer.innerHTML = ui.renderGalleryPreview(galleryItems.map((it) => it.url));
    });
  }

  const galleryInput = document.getElementById('activity-gallery-files');
  if (galleryInput) {
    galleryInput.addEventListener('change', () => {
      const files = Array.from(galleryInput.files || []);
      const container = document.getElementById('activity-gallery-preview');
      if (!container) return;
      if (!files.length) {
        // Selection cleared: restore the existing gallery preview
        container.innerHTML = ui.renderGalleryPreview(galleryItems.map((it) => it.url));
        return;
      }
      const imgs = files
        .slice(0, 12)
        .map((f) => {
          const u = URL.createObjectURL(f);
          return `<img src="${u}" alt="gallery" class="h-20 w-20 object-cover rounded"/>`;
        })
        .join('');
      container.innerHTML = imgs;
    });
  }

  // Modal backdrop close
  document.getElementById('activity-modal').addEventListener('click', (e) => {
    if (e.target.id === 'activity-modal') {
      closeActivityModal();
    }
  });

  // Translation modal events
  document.getElementById('translation-form').addEventListener('submit', handleTranslationSubmit);
  document.getElementById('cancel-translation-btn').addEventListener('click', closeTranslationModal);
  document.getElementById('translation-modal').addEventListener('click', (e) => {
    if (e.target.id === 'translation-modal') {
      closeTranslationModal();
    }
  });

  // Keyboard shortcuts
  document.addEventListener('keydown', handleKeyboardShortcuts);
};

// Configuration functions
// Configuration is env-managed; no-op
const saveConfiguration = () => { };

const testConnection = async () => {
  ui.setConnectionStatus('connecting');

  try {
    // Test backend connectivity by trying to load activities
    // The backend will handle WordPress credentials internally
    await loadActivities();

    updateState({ isConnected: true });
    ui.setConnectionStatus('connected');
    notifications.show('Connesso al backend', 'success');
  } catch (error) {
    console.error('Connection test failed:', error);
    updateState({ isConnected: false });
    ui.setConnectionStatus('disconnected');

    // Check if error is about missing WordPress credentials
    if (error.message?.includes('no_wp_credentials')) {
      notifications.show('Nessuna credenziale WordPress configurata per questo utente', 'warning');
    } else {
      notifications.show(`Errore di connessione: ${error.message}`, 'error');
    }
  }
};

// Activity management functions
const loadActivities = async () => {
  // Helper to map WordPress response to frontend format
  const mapWordPressActivity = (wp) => {
    const getMeta = (field) => {
      if (wp.meta && wp.meta[field] !== undefined) return wp.meta[field];
      if (wp[field] !== undefined) return wp[field];
      return '';
    };

    const getCategory = () => {
      if (wp._embedded?.['wp:term']?.[0]?.[0]?.slug) {
        return wp._embedded['wp:term'][0][0].slug;
      }
      return '';
    };

    const getFeaturedImage = () => {
      if (wp._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
        return wp._embedded['wp:featuredmedia'][0].source_url;
      }
      return getMeta('_thumbnail_url');
    };

    const stripHtml = (html) => html ? html.replace(/<[^>]*>/g, '') : '';
    const normalizeGallery = (gallery) => {
      if (!gallery) return '';
      return gallery.split(/[,\n]/).map(s => s.trim()).filter(Boolean).join('\n');
    };

    return {
      id: wp.id,
      nome: wp.title?.rendered || '',
      descrizione: stripHtml(wp.content?.rendered || ''),
      categoria: getCategory(),
      telefono: getMeta('_attivita_telefono'),
      email: getMeta('_attivita_email'),
      indirizzo: getMeta('_attivita_indirizzo'),
      sito_web: getMeta('_attivita_sito_web'),
      facebook: getMeta('_attivita_facebook'),
      instagram: getMeta('_attivita_instagram'),
      orari: getMeta('_attivita_orari'),
      immagine_principale: getFeaturedImage(),
      galleria: normalizeGallery(getMeta('_attivita_galleria')),
      gallery_media_ids: (getMeta('_attivita_gallery_ids') || '')
        .toString()
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      featured_media_id: wp.featured_media || null,
      status: wp.status || 'publish',
      date: wp.date || '',
      modified: wp.modified || '',
      // Translation fields
      translation_of: getMeta('_attivita_translation_of') ? parseInt(getMeta('_attivita_translation_of')) : null,
      lang: getMeta('_attivita_lang') || 'it',
      has_translation: getMeta('_attivita_has_translation') === 'true' || getMeta('_attivita_has_translation') === true
    };
  };

  updateState({ isLoading: true });
  ui.showLoading(true);

  try {
    const startTime = Date.now();

    // carica dal backend
    const activities = await backend.activities.list();

    if (!Array.isArray(activities)) {
      throw new Error('Risposta non valida dal backend');
    }

    // Check if backend already mapped the data or if it's raw WordPress
    const needsMapping = activities.length > 0 && activities[0].title && !activities[0].nome;

    const normalizedActivities = needsMapping
      ? activities.map(mapWordPressActivity)
      : activities.map(activity => ({
          ...activity,
          nome: activity.nome || '',
          descrizione: activity.descrizione || '',
          categoria: activity.categoria || '',
          telefono: activity.telefono || '',
          email: activity.email || '',
          indirizzo: activity.indirizzo || '',
          sito_web: activity.sito_web || '',
          facebook: activity.facebook || '',
          instagram: activity.instagram || '',
          orari: activity.orari || '',
          galleria: activity.galleria || '',
          gallery_media_ids: activity.gallery_media_ids || [],
          immagine_principale: activity.immagine_principale || '',
          status: activity.status || 'publish',
          // Translation fields
          translation_of: activity.translation_of || null,
          lang: activity.lang || 'it',
          has_translation: activity.has_translation || false
        }));

    updateState({
      activities: normalizedActivities,
      isLoading: false,
    });

    updateStats();
    applyFilters();

    const loadTime = Date.now() - startTime;
    notifications.show(`${activities.length} attività caricate (${loadTime}ms)`, 'success');
  } catch (error) {
    console.error('Error loading activities:', error);
    updateState({ isLoading: false });
    notifications.show(`Errore nel caricamento: ${error.message}`, 'error');
  } finally {
    ui.showLoading(false);
  }
};

const saveActivity = async (activityData) => {
  try {
    const payload = validation.normalizeActivityForBackend(activityData);
    let result;

    if (appState.editingActivity) {
      // Update esistente
      result = await backend.activities.update(appState.editingActivity.id, payload);
      notifications.show('Attività aggiornata con successo!', 'success');
    } else {
      // Create nuova
      result = await backend.activities.create(payload);
      notifications.show('Attività creata con successo!', 'success');
    }

    // ricarica lista aggiornata
    await loadActivities();
    return true;
  } catch (error) {
    console.error('Error saving activity:', error);
    notifications.show(`Errore nel salvataggio: ${error.message}`, 'error');
    return false;
  }
};

const deleteActivity = async (activityId) => {

  if (!confirm('Sei sicuro di voler eliminare questa attività?')) {
    return;
  }

  try {
    await backend.activities.remove(activityId);
    notifications.show('Attività eliminata con successo!', 'success');
    await loadActivities();
  } catch (error) {
    console.error('Error deleting activity:', error);
    notifications.show(`Errore nell'eliminazione: ${error.message}`, 'error');
  }
};

// Modal functions
const openActivityModal = (activity = null) => {
  updateState({ editingActivity: activity });
  removeFeaturedImage = false;
  galleryModified = false;
  if (activity) {
    const urls = (activity.galleria || '')
      .split(/\n|,/)
      .map((s) => s.trim())
      .filter(Boolean);
    const ids = Array.isArray(activity.gallery_media_ids) ? activity.gallery_media_ids : [];
    galleryItems = urls.map((url, i) => ({ url, id: ids[i] }));
  } else {
    galleryItems = [];
  }

  const modal = document.getElementById('activity-modal');
  const title = document.getElementById('modal-title');
  const form = document.getElementById('activity-form');

  // Reset form
  form.reset();

  if (activity) {
    // Edit mode
    title.textContent = 'Modifica Attività';
    ui.populateActivityForm(activity);
  } else {
    // Add mode
    title.textContent = 'Nuova Attività';
    // Clear previews
    const imagePrev = document.getElementById('activity-image-preview');
    if (imagePrev) imagePrev.innerHTML = '';
    const galleryPrev = document.getElementById('activity-gallery-preview');
    if (galleryPrev) galleryPrev.innerHTML = '';
  }

  modal.classList.remove('hidden');
  modal.classList.add('animate-fade-in');

  // Focus first input
  setTimeout(() => {
    document.getElementById('activity-name').focus();
  }, 100);
};

const closeActivityModal = () => {
  const modal = document.getElementById('activity-modal');
  modal.classList.add('hidden');
  modal.classList.remove('animate-fade-in');
  updateState({ editingActivity: null });
};

// Translation Modal functions
const openTranslationModal = async (activity) => {
  updateState({ translatingActivity: activity });

  const modal = document.getElementById('translation-modal');
  const form = document.getElementById('translation-form');
  const statusIndicator = document.getElementById('translation-status-indicator');
  const statusText = document.getElementById('translation-status-text');
  const loadingSpinner = document.getElementById('translation-loading-spinner');
  const saveBtn = document.getElementById('save-translation-btn');

  // Reset form
  form.reset();

  // Populate original IT data (read-only reference)
  document.getElementById('translation-original-name').textContent = activity.nome;
  document.getElementById('translation-original-category').textContent =
    `Categoria: ${getCategoryName(activity.categoria)}`;
  document.getElementById('translation-original-description').textContent = activity.descrizione || 'Nessuna descrizione';
  document.getElementById('translation-original-hours').textContent = activity.orari || 'Non specificati';

  // Show modal
  modal.classList.remove('hidden');
  modal.classList.add('animate-fade-in');

  // Show loading indicator
  statusIndicator.classList.remove('hidden');
  statusText.textContent = 'Caricamento traduzione esistente...';
  loadingSpinner.classList.remove('hidden');

  // Try to load existing translation
  let hasExistingTranslation = false;
  try {
    const translation = await backend.activities.getTranslation(activity.id, 'en');
    if (translation) {
      hasExistingTranslation = true;

      // Map data - try yoast_head_json first (cleaner), then fallback to raw WordPress
      const nome = translation.nome ||
                   translation.title?.rendered ||
                   translation.yoast_head_json?.title || '';

      const descrizione = translation.descrizione ||
                          translation.yoast_head_json?.description ||
                          (translation.content?.rendered || '').replace(/<[^>]*>/g, '');

      const orari = translation.orari || translation._attivita_orari || '';

      // Populate form fields
      document.getElementById('translation-name-en').value = nome;
      document.getElementById('translation-description-en').value = descrizione;
      document.getElementById('translation-hours-en').value = orari;

      // Update status indicator
      loadingSpinner.classList.add('hidden');
      statusText.innerHTML = '<span class="text-green-600 font-medium">✓ Traduzione esistente trovata - Modifica i campi per aggiornarla</span>';
      saveBtn.textContent = 'Aggiorna Traduzione';
    }
  } catch (error) {
    // No translation exists yet, leave form empty
    hasExistingTranslation = false;
  }

  // Update UI based on whether translation exists
  if (!hasExistingTranslation) {
    loadingSpinner.classList.add('hidden');
    statusText.innerHTML = '<span class="text-blue-600 font-medium">ℹ Nessuna traduzione esistente - Crea una nuova traduzione</span>';
    saveBtn.textContent = 'Crea Traduzione';
  }

  // Focus first input
  setTimeout(() => {
    document.getElementById('translation-name-en').focus();
  }, 100);
};

const closeTranslationModal = () => {
  const modal = document.getElementById('translation-modal');
  const statusIndicator = document.getElementById('translation-status-indicator');

  modal.classList.add('hidden');
  modal.classList.remove('animate-fade-in');
  statusIndicator.classList.add('hidden');

  updateState({ translatingActivity: null });
};

// Helper to get category name from slug
const getCategoryName = (slug) => {
  // Use categoriesService for dynamic lookup
  return categoriesService.getCategoryName(slug);
};

// Handle translation form submit
const handleTranslationSubmit = async (e) => {
  e.preventDefault();

  const activity = appState.translatingActivity;
  if (!activity) return;

  const formData = new FormData(e.target);
  const translationData = {
    nome: formData.get('nome_en'),
    descrizione: formData.get('descrizione_en'),
    orari: formData.get('orari_en') || ''
  };

  // Validate translation data
  const validationResult = validation.validateTranslationData(translationData);
  if (!validationResult.isValid) {
    notifications.show(`Errore: ${validationResult.errors.join(', ')}`, 'error');
    return;
  }

  // Show loading state
  const submitBtn = document.getElementById('save-translation-btn');
  const spinner = document.getElementById('save-translation-spinner');

  if (submitBtn) submitBtn.disabled = true;
  if (spinner) spinner.classList.remove('hidden');

  try {
    await saveTranslation(activity.id, translationData);
    notifications.show('Traduzione salvata con successo!', 'success');
    closeTranslationModal();
    await loadActivities(); // Reload to show updated badge
  } catch (error) {
    console.error('Error saving translation:', error);
    notifications.show(`Errore nel salvataggio della traduzione: ${error.message}`, 'error');
  } finally {
    if (submitBtn) submitBtn.disabled = false;
    if (spinner) spinner.classList.add('hidden');
  }
};

// Save translation (create or update)
const saveTranslation = async (sourceId, translationData) => {
  try {
    const result = await backend.activities.createOrUpdateTranslation(sourceId, translationData, 'en');
    return result;
  } catch (error) {
    console.error('Error in saveTranslation:', error);
    throw error;
  }
};

// Event handlers
const handleActivitySubmit = async (e) => {
  e.preventDefault();

  const formData = new FormData(e.target);
  const activityData = Object.fromEntries(formData.entries());

  // Extract files (if any)
  const imageFile = document.getElementById('activity-image-file')?.files?.[0] || null;
  const galleryFiles = Array.from(
    document.getElementById('activity-gallery-files')?.files || []
  );

  // Validate form data
  // For file-based flow, skip URL validations by clearing URL fields before validate,
  // they will be filled after upload
  const tempDataForValidation = { ...activityData };
  if (imageFile) tempDataForValidation.immagine_principale = '';
  if (galleryFiles.length) tempDataForValidation.galleria = '';
  const validationResult = validation.validateActivityData(tempDataForValidation);
  if (!validationResult.isValid) {
    notifications.show(
      `Errori di validazione: ${validationResult.errors.join(', ')}`,
      'error'
    );
    return;
  }

  // Disable submit button during save
  const submitBtn = document.getElementById('save-activity-btn');
  submitBtn.disabled = true;
  submitBtn.innerHTML = `
    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
    Salvataggio...
  `;

  // Featured image removed via X button (and no replacement selected):
  // featured_media_id = 0 tells WordPress to detach the featured image
  if (!imageFile && removeFeaturedImage && appState.editingActivity) {
    activityData.featured_media_id = 0;
    activityData.immagine_principale = '';
  }

  // Gallery images removed via X button (and no new selection):
  // send the remaining list — an empty string clears the whole gallery
  if (!galleryFiles.length && galleryModified && appState.editingActivity) {
    activityData.galleria = galleryItems.map((it) => it.url).join('\n');
    activityData.gallery_media_ids = galleryItems.every((it) => it.id)
      ? galleryItems.map((it) => it.id)
      : [];
  }

  // If there are files, upload them first to WP Media
  try {
    if (imageFile) {
      // Carica su backend, che a sua volta usa WP
      const uploaded = await backend.media.upload(imageFile, {
        title: activityData.nome || imageFile.name,
      });
      activityData.featured_media_id = uploaded.id;
      activityData.immagine_principale = uploaded.source_url || '';
    }

    if (galleryFiles.length) {
      const uploadResponse = await backend.media.uploadMultiple(
        galleryFiles,
        { titlePrefix: activityData.nome || 'Galleria' }
      );

      // Handle new bulk upload response format
      const uploadedGallery = uploadResponse.results || [];

      // Show errors if any
      if (uploadResponse.errors && uploadResponse.errors.length > 0) {
        const errorMessages = uploadResponse.errors.map(e => `${e.filename}: ${e.message}`).join('\n');
        notifications.show(`Alcuni file non sono stati caricati:\n${errorMessages}`, 'warning');
      }

      activityData.galleria = uploadedGallery
        .map((m) => m.source_url)
        .filter(Boolean)
        .join('\n');
      activityData.gallery_media_ids = uploadedGallery.map((m) => m.id);
    }
  } catch (uploadErr) {
    notifications.show(`Errore upload immagini: ${uploadErr.message}`, 'error');
    return;
  }


  const success = await saveActivity(activityData);

  // Re-enable submit button
  submitBtn.disabled = false;
  submitBtn.innerHTML = `
    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
    </svg>
    Salva Attività
  `;

  if (success) {
    closeActivityModal();
  }
};

const handleCategoryFilter = (e) => {
  updateState({ currentFilter: e.target.value });
  applyFilters();
};

const handleSearch = (e) => {
  updateState({ searchTerm: e.target.value });
  applyFilters();
};

const handleKeyboardShortcuts = (e) => {
  // Ctrl/Cmd + N = New Activity
  if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
    e.preventDefault();
    openActivityModal();
  }

  // Escape = Close Modal
  if (e.key === 'Escape') {
    closeActivityModal();
  }

  // Ctrl/Cmd + S = Save (when modal is open)
  if (
    (e.ctrlKey || e.metaKey) &&
    e.key === 's' &&
    !document.getElementById('activity-modal').classList.contains('hidden')
  ) {
    e.preventDefault();
    document.getElementById('activity-form').dispatchEvent(new Event('submit'));
  }
};

// Export function
const exportActivities = () => {
  if (appState.filteredActivities.length === 0) {
    notifications.show('Nessuna attività da esportare', 'warning');
    return;
  }

  const csvContent = ui.generateCSV(appState.filteredActivities);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `attivita_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notifications.show('Export completato con successo!', 'success');
  }
};

// Render function
const renderApp = () => {
  // Update stats
  ui.updateStats(appState.stats);

  // Update activities table
  if (appState.isLoading) {
    ui.showLoading(true);
  } else if (appState.filteredActivities.length === 0) {
    ui.showEmptyState(appState.activities.length === 0);
  } else {
    ui.renderActivitiesTable(appState.filteredActivities, {
      onEdit: openActivityModal,
      onDelete: deleteActivity,
    });
  }
};

// Global functions for HTML onclick handlers
window.editActivity = openActivityModal;
window.translateActivity = openTranslationModal;
window.deleteActivity = deleteActivity;

// Initialize app when DOM is loaded
document.addEventListener('DOMContentLoaded', initializeApp);

// Export for testing
export { appState, updateState, loadActivities, saveActivity };
